import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router";

import { rememberOrganization } from "@/auth/organization";
import { AuthLayout } from "@/components/layout";
import { Alert, Button, ErrorAlert, FullPageSpinner } from "@/components/ui";
import { identity } from "@/lib/api";
import { useOnce } from "@/pages/auth/helpers";

/** Invitation e-mails link here; the user must be signed in (RequireAuth wraps this route). */
export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const accept = useMutation({
    mutationFn: () => identity.acceptInvitation(token),
    onSuccess: async (org) => {
      rememberOrganization(org.id);
      await qc.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
  useOnce(() => {
    if (token) accept.mutate();
  });
  return (
    <AuthLayout title="Join organization">
      {!token && <Alert tone="danger">This invitation link is incomplete.</Alert>}
      {accept.isPending && <FullPageSpinner />}
      {accept.error && (
        <div className="space-y-3">
          <ErrorAlert error={accept.error} />
          <p className="text-sm text-fg-muted">
            Invitations are tied to the e-mail they were sent to and expire after 7 days.
          </p>
        </div>
      )}
      {accept.isSuccess && (
        <div className="space-y-4 text-center">
          <CheckCircle2 className="mx-auto size-10 text-brand-600" />
          <p className="text-sm text-fg-muted">You joined {accept.data.name}.</p>
          <Button className="w-full" onClick={() => navigate("/", { replace: true })}>
            Go to dashboard
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}
